import { Component, forwardRef, Input } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CustomerService } from 'src/app/services/customer.service';

/**
 * Seletor de cliente com busca assíncrona (autocomplete) a partir de 3 caracteres.
 * Substitui dropdowns que carregavam a lista inteira de clientes da marina.
 *
 * O valor do controle é o `customerId` (number), então é usável direto com
 * [(ngModel)] ou formControlName apontando para o id do cliente.
 *
 * Ex.: <app-customer-search-select [(ngModel)]="filter.currentCustomerId">
 */
@Component({
  selector: 'app-customer-search-select',
  templateUrl: './customer-search-select.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomerSearchSelectComponent),
      multi: true,
    },
  ],
})
export class CustomerSearchSelectComponent implements ControlValueAccessor {
  @Input() placeholder = 'Digite pelo menos 3 caracteres (nome)...';
  @Input() tipoBusca = 'NOME';
  @Input() minLength = 3;
  /** Pausa (ms) na digitação antes de disparar a consulta (debounce). */
  @Input() delay = 500;

  filteredCustomers: { label: string; value: number }[] = [];
  displayValue: any = '';
  disabled = false;

  private onChange: (value: number) => void = () => {};
  onTouched: () => void = () => {};

  constructor(private customerService: CustomerService) {}

  writeValue(value: number): void {
    // Casos de uso são filtros que começam vazios; ao limpar o id externamente,
    // limpamos o texto exibido. Não há resolução reversa id -> nome.
    if (value == null) {
      this.displayValue = '';
      this.filteredCustomers = [];
    }
  }

  registerOnChange(fn: (value: number) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  searchCustomers(event: { query: string }): void {
    const query = event.query;
    if (!query || query.length < this.minLength) {
      this.filteredCustomers = [];
      return;
    }
    this.customerService
      .listCustomerToInviteWithFilter(query, this.tipoBusca)
      .subscribe({
        next: (data) => {
          const seen = new Set<number>();
          this.filteredCustomers = data
            .map((c: any) => ({
              label: c.customerName || c.name || '',
              value: c.customerId,
            }))
            .filter((c) => {
              if (c.value == null || seen.has(c.value)) {
                return false;
              }
              seen.add(c.value);
              return true;
            });
        },
        error: () => {
          this.filteredCustomers = [];
        },
      });
  }

  onSelect(event: any): void {
    if (event && event.value !== undefined) {
      this.displayValue = event.label ?? '';
      this.onChange(event.value);
      this.onTouched();
    }
  }

  onClear(): void {
    this.displayValue = '';
    this.filteredCustomers = [];
    this.onChange(null);
    this.onTouched();
  }
}
